import { z } from "zod";

export class ValidationError extends Error {
  validationErrors: Record<string, string>;
  name: "ValidationError" = "ValidationError" as const;

  constructor(message: string, validationErrors: Record<string, string>) {
    super(message);
    this.validationErrors = validationErrors;
  }
}

export class NetworkError extends Error {
  name: "NetworkError" = "NetworkError" as const;

  constructor(message: string) {
    super(message);
  }
}

export type FormSubmissionError = ApiError | ValidationError | NetworkError;

// Zod schemas to match with the types above
// TODO: Move to an inference approach for the types rather than the current approach of it just being manually typed out (double handling bad!)
export const newPatientSubmissionPayloadSchema = z.object({
  condition: z.number(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  sex: z.string(),
  dob: z.string(),
  country: z.string(),
  suburb: z.string(),
  address: z.string().optional(),
  phoneNumber: z.string(),
  localeCode: z.string(),
  ketchConsent: z.string().optional(),
  attributeValueInput: z.array(
    z.object({
      attributeDefinitionId: z.number(),
      isSkipped: z.boolean().optional(),
      booleanAttributeValue: z.boolean().optional(),
      floatAttributeValue: z.number().optional(),
      radioAttributeValue: z.string().optional(),
      medicationAttributeValue: z
        .object({
          hasTaken: z.boolean(),
          intervals: z
            .array(
              z.object({
                start: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
                end: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
              }),
            )
            .optional(),
        })
        .optional(),
      medicationGroupAttributeValue: z
        .object({
          hasTaken: z.boolean(),
          intervals: z
            .array(
              z.object({
                start: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
                end: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
              }),
            )
            .optional(),
        })
        .optional(),
    }),
  ),
  consentToHmTCsVersion: z.string(),
  thirdPartyContactConsentVersion: z.string(),
  consentLocaleCode: z.string(),
  attribution: z.record(z.string(), z.unknown()).optional(),
  preferredLoginMethod: z.enum(["EMAIL", "SMS"]).optional(),
  trialSiteId: z.string(),
  emrConsent: z
    .object({
      consentSignedAt: z.string(),
      consentTemplateVersion: z.number(),
    })
    .optional(),
});

export const existingPatientSubmissionPayloadSchema = z.object({
  userCredentials: z
    .object({
      email: z.string().optional(),
      loginCode: z.string().optional(),
      googleJwtToken: z.string().optional(),
      oneClickLogin: z
        .object({
          oneClickLoginCode: z.string(),
        })
        .optional(),
    })
    .optional(),
  condition: z.number(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  sex: z.string().optional(),
  dob: z.string(),
  suburb: z.string(),
  country: z.string(),
  address: z.string().optional(),
  phoneNumber: z.string(),
  localeCode: z.string(),
  ketchConsent: z.string().optional(),
  attributeValueInput: z.array(
    z.object({
      attributeDefinitionId: z.number(),
      isSkipped: z.boolean().optional(),
      booleanAttributeValue: z.boolean().optional(),
      floatAttributeValue: z.number().optional(),
      radioAttributeValue: z.string().optional(),
      medicationAttributeValue: z
        .object({
          hasTaken: z.boolean(),
          intervals: z
            .array(
              z.object({
                start: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
                end: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
              }),
            )
            .optional(),
        })
        .optional(),
      medicationGroupAttributeValue: z
        .object({
          hasTaken: z.boolean(),
          intervals: z
            .array(
              z.object({
                start: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
                end: z.object({
                  year: z.number().optional(),
                  month: z.number().optional(),
                  day: z.number().optional(),
                  current: z.boolean().optional(),
                  isBeforeX: z.boolean().optional(),
                }),
              }),
            )
            .optional(),
        })
        .optional(),
    }),
  ),
  preferredLoginMethod: z.enum(["EMAIL", "SMS"]).optional(),
  trialSiteId: z.string(),
  consentToHmTCsVersion: z.string(),
  thirdPartyContactConsentVersion: z.string(),
  consentLocaleCode: z.string(),
  emrConsent: z
    .object({
      consentSignedAt: z.string(),
      consentTemplateVersion: z.number(),
    })
    .optional(),
});

export const trialSiteInboundCallingConfigSchema = z.object({
  enabled: z.boolean(),
  phoneNumber: z.string(),
});

export const trialSiteDetailsSchema = z.object({
  name: z.string().optional(),
  address: z.string().optional(),
  state: z.string().optional(),
  countryCode: z.string().optional(),
  inboundCallingConfig: trialSiteInboundCallingConfigSchema.optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

// Maps to ReferPatientOutcome in the backend
export const apiResponseOutcomeSchema = z.object({
  existingPatient: z.boolean(),
  // If this is false that means that the patient was created successfully but their zipcode/postcode was out of range (AKA no trial match)
  successfullyCreatedReferral: z.boolean(),
  conditionIdHint: z.number().optional(),
  emailHint: z.string().nullish(),
  requireLogin: z.boolean().nullish(),
  // If this is false that means that the patient was created successfully but their zipcode/postcode was out of range (AKA no trial match)
  trialSiteDetails: trialSiteDetailsSchema.nullish(),
  patientId: z.coerce.number().nullish(),
  applicationSiteId: z.coerce.number().nullish(),
  loginType: z.enum(["SEND_LOGIN_CODE_EMAIL", "SEND_LOGIN_CODE_SMS"]).nullish(),
  loginHint: z.string().nullish(),
  smsLoginSupported: z.boolean().optional(),
  oneClickLoginCode: z.string().nullish(),
});

export const apiResponseSchema = z.object({
  status: z.enum(["success", "needs_login", "error"]),
  outcome: apiResponseOutcomeSchema,
});

// Nullable API response schema
export const apiResponseNullableSchema = z.union([apiResponseSchema, z.null()]);

// For error types
export const apiErrorSchema = z.object({
  type: z.literal("api"),
  message: z.string(),
  status: z.number().optional(),
  response: z
    .union([
      z
        .object({
          message: z.string().optional(),
          error: z.string().optional(),
          errors: z
            .union([
              z.array(z.string()),
              z.array(z.object({ message: z.string() })),
            ])
            .optional(),
          status: z.number().optional(),
        })
        .passthrough(),
      z.null(),
    ])
    .optional(),
});

export const validationErrorSchema = z.object({
  type: z.literal("validation"),
  message: z.string(),
  validationErrors: z.record(z.string()),
});

export const networkErrorSchema = z.object({
  type: z.literal("network"),
  message: z.string(),
});

export const formSubmissionErrorSchema = z.union([
  apiErrorSchema,
  validationErrorSchema,
  networkErrorSchema,
]);

export type ApiResponseOutcome = z.infer<typeof apiResponseOutcomeSchema>;

export interface ApiResponse {
  status: "success" | "needs_login" | "error";
  outcome: ApiResponseOutcome;
}

export type NewPatientSubmissionPayload = z.infer<
  typeof newPatientSubmissionPayloadSchema
>;

export type ExistingPatientSubmissionPayload = z.infer<
  typeof existingPatientSubmissionPayloadSchema
>;

// API error response type - can include API response structure or error details
export interface ApiErrorResponse {
  message?: string;
  error?: string;
  errors?: string[] | Array<{ message: string }>;
  status?: number;
  outcome?: ApiResponseOutcome; // API responses can include outcome even in error cases
  [key: string]: unknown;
}

// API payload interface
export class ApiError extends Error {
  status?: number;
  response?: ApiErrorResponse | null;
  payload: NewPatientSubmissionPayload | ExistingPatientSubmissionPayload;
  name: "ApiError" = "ApiError" as const;

  constructor(
    message: string,
    status?: number,
    response?: ApiErrorResponse | null,
    payload?: NewPatientSubmissionPayload | ExistingPatientSubmissionPayload,
  ) {
    super(message);
    this.status = status;
    this.response = response;
    this.payload = payload as
      | NewPatientSubmissionPayload
      | ExistingPatientSubmissionPayload;
  }
}
